/**
 * usePMAutoGeneration — Automatically creates PM work orders when plans become overdue.
 *
 * Runs on mount + every 5 minutes. For each PM plan that is overdue and has no
 * open preventive WO for that mold, creates a PM work order and marks the plan
 * with `last_auto_wo_created_at` to prevent duplicates.
 *
 * Only triggers for the first user to open the dashboard per org (debounced by
 * checking `last_auto_wo_created_at` within the last hour).
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { fetchPMPlans } from '@/lib/api/molds';
import { createWorkOrderInDB } from '@/lib/api/workOrders';
import type { WorkOrder } from '@/types/maintenance';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_WINDOW_MS = 60 * 60 * 1000; // 1 hour — skip if auto-WO was created within this window

export function usePMAutoGeneration(
  workOrders: WorkOrder[],
  enabled: boolean = true,
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const runningRef = useRef(false);

  useEffect(() => {
    if (!enabled || !user?.organization_id || !user?.id) return;

    const orgId = user.organization_id;
    const userId = user.id;

    async function checkAndGenerate() {
      // Prevent concurrent runs
      if (runningRef.current) return;
      runningRef.current = true;

      try {
        const plans = await fetchPMPlans(orgId);
        const now = new Date();
        let created = 0;

        for (const plan of plans) {
          if (!plan.active || !plan.mold_id) continue;
          if (!plan.is_overdue) continue;

          // Debounce: skip if auto-WO was created recently
          if (plan.last_auto_wo_created_at) {
            const lastAuto = new Date(plan.last_auto_wo_created_at).getTime();
            if (now.getTime() - lastAuto < DEBOUNCE_WINDOW_MS) continue;
          }

          // Check if an open preventive WO already exists for this mold
          const hasOpenPM = workOrders.some(
            wo => wo.mold_id === plan.mold_id && wo.type === 'preventive' && wo.status !== 'closed',
          );
          if (hasOpenPM) continue;

          // Create the PM work order
          try {
            await createWorkOrderInDB(
              {
                type: 'preventive',
                mold_id: plan.mold_id,
                impact: 'pm',
                problem_description: 'Preventive maintenance — auto-generated (PM overdue)',
                parts_needed: false,
                from_planning: true,
              },
              userId,
              orgId,
            );

            // Mark the plan so we don't re-create within the debounce window
            await supabase
              .from('pm_plans')
              .update({ last_auto_wo_created_at: now.toISOString() })
              .eq('id', plan.id);

            created++;
          } catch (err) {
            console.warn(`Failed to auto-create PM WO for plan ${plan.id}:`, err);
          }
        }

        if (created > 0) {
          toast.success(`${created} PM work order${created > 1 ? 's' : ''} auto-created`);
          queryClient.invalidateQueries({ queryKey: ['work_orders'] });
          queryClient.invalidateQueries({ queryKey: ['pm_plans'] });
        }
      } catch (err) {
        console.warn('PM auto-generation check failed:', err);
      } finally {
        runningRef.current = false;
      }
    }

    // Run on mount (slight delay to let initial data load)
    const initialTimer = setTimeout(checkAndGenerate, 3000);

    // Run periodically
    const interval = setInterval(checkAndGenerate, CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, [enabled, user?.organization_id, user?.id, workOrders, queryClient]);
}
